/**
 * Shared predicate behind MemberManagement's membership-type dropdown and its
 * quick-filter stat cards (#148), so the two can never disagree.
 *
 * ADULT and JUNIOR are computed from date of birth via `isJuniorForYear`
 * (#127 age rule, D7) rather than the stored `membershipType` string or any
 * stored `isJunior` flag, so a member whose stored type is stale (for
 * example a JUNIOR who has turned 17 since their last renewal) is filtered
 * by their real age, not by what was written to the row at registration or
 * last renewal.
 *
 * FAMILY and SPONSOR are unchanged from the existing quick-filter logic:
 * FAMILY matches membershipType 'FAMILY' or any row carrying a
 * familyGroupId at all (so a grouped ADULT/JUNIOR with a family group counts
 * as a family member even if its membershipType predates grouping or is
 * stale). SPONSOR matches the sponsor's own business record only (solo, or
 * the family's primary), never their family members.
 */

import { isJuniorForYear, clubYear } from '@shared/membership';
import type { Member } from '../components/admin/MemberManagement';

export type MembershipFilterId = 'ALL' | 'ADULT' | 'JUNIOR' | 'FAMILY' | 'SPONSOR';

const SPONSOR_TYPES = ['SPONSOR_GOLD', 'SPONSOR_SILVER', 'SPONSOR_BRONZE'];

/**
 * `member` only needs the fields this predicate reads; callers (including
 * tests) don't have to construct a full `Member`.
 */
export type MembershipFilterMember = Pick<Member, 'membershipType' | 'dateOfBirth' | 'familyPrimary'> & {
  familyGroupId?: string;
};

/**
 * True when `member` matches `filter`. `now` defaults to the current time
 * and only affects ADULT/JUNIOR (via the club-year age rule) — pass it in
 * tests to pin the membership year.
 */
export function matchesMembershipFilter(
  member: MembershipFilterMember,
  filter: MembershipFilterId,
  now: Date = new Date()
): boolean {
  const familyGroupId = member.familyGroupId;

  switch (filter) {
    case 'ADULT':
      return !isJuniorForYear(member.dateOfBirth, clubYear(now));
    case 'JUNIOR':
      return isJuniorForYear(member.dateOfBirth, clubYear(now));
    case 'FAMILY':
      // See matchesQuickFilter's FAMILY case: a sponsor who registered with
      // a family roster is still a family, not just a sponsor.
      return member.membershipType === 'FAMILY' || !!familyGroupId;
    case 'SPONSOR':
      // The sponsor's own business record only — see matchesQuickFilter's
      // SPONSOR case for why familyPrimary/familyGroupId distinguish it
      // from the sponsor's own family members.
      return SPONSOR_TYPES.includes(member.membershipType) && (!familyGroupId || !!member.familyPrimary);
    case 'ALL':
    default:
      return true;
  }
}
